/**
 * P-19a — D-146 (Q-96 / Q-103): the statuses of a service request ("קריאה",
 * task_type = 'ticket'), separate from a task's. FRONTEND MIRROR of
 * ote-backend/src/services/ticketStatus.shared.ts — the server file is the
 * source of truth; a unit test (ticketStatusShared.test.ts) keeps the two equal.
 *
 *   new              חדשה           a ticket was opened
 *   in_progress      בטיפול         "התחלת עבודה" (the customer sees it)
 *   waiting_customer ממתינה ללקוח   "שליחת עדכון ללקוח" + "מחכה לתשובה"
 *   completed        הושלמה         "סיום עבודה" — "הטיפול בקריאה הושלם"
 *   closed           נסגרה          the customer approved, or 7 days with no reply
 *
 * A customer reply on completed / closed (and on waiting_customer) moves the
 * ticket back to in_progress. Tasks and discussions keep the task statuses.
 */

export const TICKET_STATUSES = ['new', 'in_progress', 'waiting_customer', 'completed', 'closed'] as const;
export type TicketStatusKey = (typeof TICKET_STATUSES)[number];

/** The statuses of a task / discussion (unchanged by D-146). */
export const TASK_STATUSES = ['planning', 'in_progress', 'in_review', 'testing', 'completed'] as const;
export type TaskStatusKey = (typeof TASK_STATUSES)[number];

/** Every key `tickets.status` may hold (the union, for request validators). */
export const ALL_STATUS_KEYS: readonly string[] = Array.from(new Set<string>([...TASK_STATUSES, ...TICKET_STATUSES]));

export const TICKET_STATUS_LABELS_HE: Record<TicketStatusKey, string> = {
  new: 'חדשה',
  in_progress: 'בטיפול',
  waiting_customer: 'ממתינה ללקוח',
  completed: 'הושלמה',
  closed: 'נסגרה',
};

export const TICKET_STATUS_LABELS_EN: Record<TicketStatusKey, string> = {
  new: 'New',
  in_progress: 'In progress',
  waiting_customer: 'Waiting for customer',
  completed: 'Completed',
  closed: 'Closed',
};

/** Done = no more work (completed waits up to 7 days for the customer; closed is final). */
export const DONE_STATUSES = ['completed', 'closed'] as const;
export const TICKET_OPEN_STATUSES: readonly TicketStatusKey[] = ['new', 'in_progress', 'waiting_customer'];

/** SQL list for `status IN ${DONE_STATUSES_SQL}` / `NOT IN` (tasks never hold 'closed'). */
export const DONE_STATUSES_SQL = `('completed', 'closed')`;

/** v30's mapping of an existing ticket's (task-set) status. */
export const LEGACY_TO_TICKET_STATUS: Record<string, TicketStatusKey> = {
  planning: 'new',
  in_progress: 'in_progress',
  in_review: 'in_progress',
  testing: 'in_progress',
  completed: 'completed',
};

/** What a ticket-only status becomes on a task / discussion (the v30 trigger, mirrored). */
export const TICKET_TO_TASK_STATUS: Record<string, TaskStatusKey> = {
  new: 'planning',
  waiting_customer: 'in_progress',
  closed: 'completed',
};

/** "הושלמה" with no customer reply for this many days becomes "נסגרה" (D-146). */
export const AUTO_CLOSE_DAYS = 7;

/** The customer message on "סיום עבודה" (D-146: not "הבעיה נפתרה"). */
export const COMPLETED_CUSTOMER_MESSAGE_HE = 'הטיפול בקריאה הושלם';

export function isTicketStatus(s: string | null | undefined): s is TicketStatusKey {
  return !!s && (TICKET_STATUSES as readonly string[]).includes(s);
}

export function isDoneStatus(s: string | null | undefined): boolean {
  return s === 'completed' || s === 'closed';
}

/** Is this a service request ('ticket'; a missing type is the legacy default)? */
export function isServiceTicket(taskType: string | null | undefined): boolean {
  return (taskType || 'ticket') === 'ticket';
}

/**
 * D-154 (QX-85) — a discussion has TWO statuses: "פתוח" (open) / "סגור"
 * (closed), held as in_progress / completed (the task set, so the v30 trigger
 * and every done-status rule are unchanged). An older discussion still at
 * planning / in_review / testing reads as open.
 */
export const DISCUSSION_STATUSES = ['in_progress', 'completed'] as const;
export const DISCUSSION_STATUS_LABELS_HE: Record<(typeof DISCUSSION_STATUSES)[number], string> = {
  in_progress: 'פתוח',
  completed: 'סגור',
};

export function isDiscussion(taskType: string | null | undefined): boolean {
  return taskType === 'discussion';
}

/** The status a row of this kind will hold (the same rule as the v30 trigger; a discussion: open / closed). */
export function normalizeStatusForType(status: string, taskType: string | null | undefined): string {
  if (isServiceTicket(taskType)) return LEGACY_TO_TICKET_STATUS[status] ?? status;
  if (isDiscussion(taskType)) return isDoneStatus(status) ? 'completed' : 'in_progress';
  return TICKET_TO_TASK_STATUS[status] ?? status;
}

/** The statuses offered for a row of this kind. */
export function statusesForType(taskType: string | null | undefined): readonly string[] {
  if (isDiscussion(taskType)) return DISCUSSION_STATUSES;
  return isServiceTicket(taskType) ? TICKET_STATUSES : TASK_STATUSES;
}

/** Statuses a customer reply moves back to "בטיפול" (D-146). */
export const CUSTOMER_REPLY_REOPENS: readonly TicketStatusKey[] = ['completed', 'closed', 'waiting_customer'];

// ---- D-146 (Q-102, P-19a item 6) — where a ticket came from --------------------------
/** tickets.source (v30 CHECK). 'manual' = not marked (legacy / internal). */
export const TICKET_SOURCES = ['phone', 'email', 'whatsapp', 'meeting', 'portal', 'manual', 'bug_report', 'integration', 'extension'] as const;
export type TicketSource = (typeof TICKET_SOURCES)[number];
/** What an employee may mark when opening a ticket on the customer's behalf. */
export const STAFF_TICKET_SOURCES: readonly TicketSource[] = ['phone', 'email', 'whatsapp', 'meeting'];
export const TICKET_SOURCE_LABELS_HE: Record<TicketSource, string> = {
  phone: 'טלפון',
  email: 'מייל',
  whatsapp: 'וואטסאפ',
  meeting: 'פגישה',
  portal: 'פורטל',
  manual: 'ידני',
  bug_report: 'דיווח באג',
  integration: 'אינטגרציה',
  extension: 'תוסף',
};
